import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

const ModulosContext = createContext(null);

const DEFAULT_MODULES = {
  financiero: true,
  caja_chica: true,
  asistencia: true,
  incidencias: true,
  documentos: true,
  checklists: true,
  alertas: true,
  acuerdos: true,
  minutas: true,
  reportes: true,
  proveedores: true,
  cotizaciones: true,
  soporte_ar: true,
  mantenimientos: true,
  areas_comunes: true,
  gestion_corporativa: true,
  inventario: true,
  atencion_vecinal: true,
  access_control: true,
};

export const useModulos = () => {
  const context = useContext(ModulosContext);
  if (!context) {
    throw new Error('useModulos must be used within a ModulosProvider');
  }
  return context;
};

export const ModulosProvider = ({ children }) => {
  const { user, getAvailableModulos, getDefaultCondominioId, isDireccion } = useAuth();
  const [modulosActuales, setModulosActuales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [condominioActivo, setCondominioActivo] = useState(null);

  useEffect(() => {
    if (!user) {
      setModulosActuales(null);
      setCondominioActivo(null);
      setLoading(false);
      return;
    }

    if (isDireccion()) {
      setModulosActuales({ ...DEFAULT_MODULES });
      setCondominioActivo(null);
      setLoading(false);
      return;
    }

    const available = Array.isArray(getAvailableModulos?.()) ? getAvailableModulos() : [];
    const derivedModules = {
      ...DEFAULT_MODULES,
      ...Object.fromEntries(Object.keys(DEFAULT_MODULES).map((modulo) => [modulo, available.includes(modulo)])),
    };

    setModulosActuales(derivedModules);
    setCondominioActivo(getDefaultCondominioId?.() || user.condominios_ids?.[0] || null);
    setLoading(false);
  }, [user, getAvailableModulos, getDefaultCondominioId, isDireccion]);

  const isModuloHabilitado = useMemo(
    () => (modulo) => {
      if (loading || !modulosActuales) {
        return true;
      }
      return modulosActuales[modulo] ?? true;
    },
    [loading, modulosActuales]
  );

  const getModulosActivos = useMemo(
    () => () => modulosActuales || { ...DEFAULT_MODULES },
    [modulosActuales]
  );

  const reloadModulos = () => {
    setLoading(true);
    if (!user) {
      setLoading(false);
    } else {
      const available = Array.isArray(getAvailableModulos?.()) ? getAvailableModulos() : [];
      setModulosActuales({
        ...DEFAULT_MODULES,
        ...Object.fromEntries(Object.keys(DEFAULT_MODULES).map((modulo) => [modulo, isDireccion() ? true : available.includes(modulo)])),
      });
      setCondominioActivo(getDefaultCondominioId?.() || user.condominios_ids?.[0] || null);
      setLoading(false);
    }
  };

  const value = {
    modulosActuales,
    condominioActivo,
    loading,
    isModuloHabilitado,
    getModulosActivos,
    reloadModulos,
  };

  return (
    <ModulosContext.Provider value={value}>
      {children}
    </ModulosContext.Provider>
  );
};

export default ModulosContext;
